import { z } from 'zod';
import { emailSchema, personNameSchema } from './accounts.js';
import { editionSummarySchema } from './editions.js';
import { CHILD_MAX_AGE, CHILD_MIN_AGE, ageInYear } from './participants.js';

// --- Słowniki (BR-21, BR-23) ---

/** BR-23: dla kogo są zajęcia. */
export const audiences = ['children', 'children_adults', 'adults'] as const;
export const audienceSchema = z.enum(audiences);
export type Audience = z.infer<typeof audienceSchema>;

/** BR-21, BR-60: kategoria statystyk typu zajęć. */
export const statsCategories = ['workshop', 'talk', 'other'] as const;
export const statsCategorySchema = z.enum(statsCategories);
export type StatsCategory = z.infer<typeof statsCategorySchema>;

export const activityStatuses = ['active', 'cancelled'] as const;
export type ActivityStatus = (typeof activityStatuses)[number];

const text = (max: number) =>
  z.string().trim().min(1, { message: 'To pole jest wymagane.' }).max(max, { message: `Maksymalnie ${max} znaków.` });
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, { message: `Maksymalnie ${max} znaków.` })
    .optional()
    .nullable()
    .transform((value) => (value ? value : null));
const localTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, { message: 'Podaj godzinę (GG:MM).' });
const intFromForm = (min: number, max: number) =>
  z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z
      .number({ message: 'Podaj liczbę.' })
      .int({ message: 'Podaj liczbę całkowitą.' })
      .min(min, { message: `Nie mniej niż ${min}.` })
      .max(max, { message: `Nie więcej niż ${max}.` }),
  );
const optionalAge = z.preprocess(
  (value) => (value === '' || value === null || value === undefined ? null : Number(value)),
  z
    .number({ message: 'Podaj wiek.' })
    .int({ message: 'Podaj wiek.' })
    .min(CHILD_MIN_AGE, { message: `Wiek od ${CHILD_MIN_AGE} do ${CHILD_MAX_AGE}.` })
    .max(CHILD_MAX_AGE, { message: `Wiek od ${CHILD_MIN_AGE} do ${CHILD_MAX_AGE}.` })
    .nullable(),
);

// --- Typy zajęć (US-304) ---

export const activityTypeInputSchema = z.object({
  name: text(60),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/, { message: 'Kolor w postaci #RRGGBB.' }),
  hasEnrollment: z.boolean(),
  blocksTime: z.boolean(),
  statsCategory: statsCategorySchema,
  /** Nazwy alternatywne z importu (np. „Warsztat zamknięty”). */
  aliases: z.array(text(120)).max(20),
});
export type ActivityTypeInput = z.infer<typeof activityTypeInputSchema>;

export const activityTypeSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  color: z.string(),
  hasEnrollment: z.boolean(),
  blocksTime: z.boolean(),
  statsCategory: statsCategorySchema,
  aliases: z.array(z.string()),
  activityCount: z.number().int(),
});
export type ActivityType = z.infer<typeof activityTypeSchema>;
export const activityTypeListSchema = z.object({ types: z.array(activityTypeSchema) });

// --- Sale (US-303) ---

export const roomInputSchema = z.object({ name: text(80), hidden: z.boolean() });
export type RoomInput = z.infer<typeof roomInputSchema>;
export const roomOrderSchema = z.object({ roomIds: z.array(z.string().uuid()).max(200) });
export type RoomOrder = z.infer<typeof roomOrderSchema>;

export const roomSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  sortOrder: z.number().int(),
  hidden: z.boolean(),
  activityCount: z.number().int(),
});
export type Room = z.infer<typeof roomSchema>;
export const roomListSchema = z.object({ rooms: z.array(roomSchema) });

// --- Zajęcia (US-305, US-307, US-308) ---

export const speakerInputSchema = z.object({
  email: emailSchema,
  firstName: personNameSchema,
  lastName: personNameSchema,
  bio: optionalText(2000),
});
export type SpeakerInput = z.infer<typeof speakerInputSchema>;

/**
 * Zajęcia z panelu i z importu JSON – ten sam kształt (US-305 AC-1, US-308 AC-1). Typ i sala po nazwie: brakująca sala jest
 * tworzona (BR-73), nieznany typ to błąd. Godziny są lokalne w dniu edycji (Europe/Warsaw). Rezerwa pusta = domyślna z edycji.
 */
export const activityInputSchema = z
  .object({
    symbol: z
      .string()
      .trim()
      .min(1, { message: 'Podaj symbol.' })
      .max(20, { message: 'Maksymalnie 20 znaków.' })
      .regex(/^[\p{L}\p{N}._-]+$/u, { message: 'Symbol bez spacji, np. W12.' }),
    title: text(200),
    type: text(120),
    room: text(80),
    startTime: localTimeSchema,
    endTime: localTimeSchema,
    audience: audienceSchema,
    ageMin: optionalAge.optional(),
    ageMax: optionalAge.optional(),
    capacity: intFromForm(0, 10_000),
    reserve: intFromForm(0, 1000).optional(),
    description: optionalText(20_000),
    requirements: optionalText(2000),
    speakersDisplay: optionalText(300),
    speakers: z.array(speakerInputSchema).max(10).default([]),
  })
  .superRefine((value, ctx) => {
    if (value.endTime <= value.startTime) {
      ctx.addIssue({ code: 'custom', path: ['endTime'], message: 'Koniec musi być po początku.' });
    }
    if (value.audience === 'adults' && (value.ageMin != null || value.ageMax != null)) {
      ctx.addIssue({ code: 'custom', path: ['ageMin'], message: 'Zajęcia tylko dla dorosłych nie mają wieku dzieci.' });
    }
    if (value.ageMin != null && value.ageMax != null && value.ageMin > value.ageMax) {
      ctx.addIssue({ code: 'custom', path: ['ageMax'], message: 'Wiek maksymalny nie może być mniejszy niż minimalny.' });
    }
  })
  .transform((value) => ({
    ...value,
    ageMin: value.audience === 'adults' ? null : (value.ageMin ?? null),
    ageMax: value.audience === 'adults' ? null : (value.ageMax ?? null),
    reserve: value.reserve ?? null,
  }));
export type ActivityInput = z.infer<typeof activityInputSchema>;

export const activitySpeakerSchema = z.object({
  accountId: z.string().uuid(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  bio: z.string().nullable(),
});

export const adminActivitySchema = z.object({
  id: z.string().uuid(),
  symbol: z.string(),
  title: z.string(),
  typeId: z.string().uuid(),
  type: z.string(),
  roomId: z.string().uuid(),
  room: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  audience: audienceSchema,
  ageMin: z.number().int().nullable(),
  ageMax: z.number().int().nullable(),
  capacity: z.number().int(),
  reserve: z.number().int(),
  enrolled: z.number().int(),
  status: z.enum(activityStatuses),
  cancelReason: z.string().nullable(),
  description: z.string().nullable(),
  requirements: z.string().nullable(),
  speakersDisplay: z.string().nullable(),
  speakers: z.array(activitySpeakerSchema),
  /** US-306 AC-4: usunąć można tylko zajęcia, na które nikt nigdy się nie zapisał. */
  canDelete: z.boolean(),
});
export type AdminActivity = z.infer<typeof adminActivitySchema>;
export const adminActivityListSchema = z.object({ activities: z.array(adminActivitySchema) });

export const cancelActivitySchema = z.object({ reason: text(500) });
export type CancelActivity = z.infer<typeof cancelActivitySchema>;

/** US-305 AC-6: historia zmian zajęć. */
export const activityChangeSchema = z.object({
  at: z.string(),
  actor: z.string().nullable(),
  source: z.enum(['panel', 'import']),
  action: z.enum(['create', 'update', 'cancel', 'restore']),
  fields: z.array(z.string()),
});
export const activityHistorySchema = z.object({ changes: z.array(activityChangeSchema) });

/** Konflikt zmiany zajęć z istniejącymi zapisami (US-305 AC-3, US-304 AC-3/AC-4). */
export const activityConflictSchema = z.object({
  symbol: z.string(),
  field: z.string(),
  reason: z.enum(['capacity_below_enrolled', 'age_excludes_enrolled', 'time_collision', 'type_without_enrollment', 'symbol_locked']),
  participants: z.array(z.string()),
});
export type ActivityConflict = z.infer<typeof activityConflictSchema>;

// --- Prowadzący (US-307) ---

/**
 * PRD §10 pkt 56: zaproszenia prowadzących wysyła admin z listy. `active` – hasło ustawione i e‑mail potwierdzony;
 * `invited` – wysłano zaproszenie (w tej albo innej edycji, także zaproszenie do roli); `not_invited` – konto bez maila.
 */
export const speakerInvitationStatuses = ['not_invited', 'invited', 'active'] as const;
export type SpeakerInvitationStatus = (typeof speakerInvitationStatuses)[number];

export const speakerSummarySchema = z.object({
  accountId: z.string().uuid(),
  email: z.string(),
  name: z.string(),
  status: z.enum(speakerInvitationStatuses),
  /** Ostatnie zaproszenie (ISO). */
  invitedAt: z.string().nullable(),
  activities: z.array(z.object({ symbol: z.string(), title: z.string() })),
});
export type SpeakerSummary = z.infer<typeof speakerSummarySchema>;
export const speakerListSchema = z.object({ speakers: z.array(speakerSummarySchema) });
export const speakerInvitationsResultSchema = z.object({ sent: z.number().int() });
export type SpeakerInvitationsResult = z.infer<typeof speakerInvitationsResultSchema>;

// --- Import agendy z CSV arkusza (US-310, PRD §10 pkt 48) ---

/** Treść pliku CSV pobranego z arkusza Google (Plik → Pobierz → CSV, zakładka edycji). */
export const agendaCsvImportSchema = z.object({
  dryRun: z.boolean().default(true),
  /** „Zastąp agendę”: usuń zajęcia spoza pliku, na które nikt się nie zapisywał. */
  removeMissing: z.boolean().default(false),
  csv: z.string().min(1, { message: 'Plik jest pusty.' }).max(4 * 1024 * 1024, { message: 'Plik jest za duży (maks. 4 MB).' }),
});
export type AgendaCsvImport = z.infer<typeof agendaCsvImportSchema>;

export const importErrorSchema = z.object({ symbol: z.string(), field: z.string(), reason: z.string() });
export type ImportError = z.infer<typeof importErrorSchema>;

export const importReportSchema = z.object({
  success: z.boolean(),
  dryRun: z.boolean(),
  created: z.array(z.string()),
  updated: z.array(z.object({ symbol: z.string(), fields: z.array(z.string()) })),
  unchanged: z.array(z.string()),
  /** Z-304: zajęcia edycji nieobecne w imporcie, które zostały (bez „Zastąp agendę” albo z historią zapisów). */
  missing: z.array(z.string()),
  /** Zajęcia usunięte opcją „Zastąp agendę” (raporty sprzed tej opcji nie mają pola). */
  removed: z.array(z.string()).default([]),
  newRooms: z.array(z.string()),
  newSpeakers: z.array(z.string()),
  errors: z.array(importErrorSchema),
});
export type ImportReport = z.infer<typeof importReportSchema>;

export const importSources = ['json', 'legacy', 'panel', 'co_speakers'] as const;
/**
 * PRD §10 pkt 55: współprowadzący z CSV (imię, nazwisko, e‑mail, symbol zajęć) – import tylko dopisuje prowadzących do zajęć,
 * konta zakłada bez maila (pkt 56). Pkt 58: kilka symboli w komórce, symbole szukane we wszystkich edycjach dnia, osoby bez
 * e‑maila pomijane, wątpliwe e‑maile pomijane z ostrzeżeniem.
 */
export const coSpeakersCsvImportSchema = z.object({
  dryRun: z.boolean().default(true),
  csv: agendaCsvImportSchema.shape.csv,
});
export type CoSpeakersCsvImport = z.infer<typeof coSpeakersCsvImportSchema>;

export const coSpeakersReportSchema = z.object({
  kind: z.literal('co_speakers'),
  success: z.boolean(),
  dryRun: z.boolean(),
  /** „A05: anna@x.pl” */
  added: z.array(z.string()),
  /** Osoba była już prowadzącym tych zajęć. */
  alreadyAssigned: z.array(z.string()),
  /** E‑maile kont założonych przez import. */
  newSpeakers: z.array(z.string()),
  errors: z.array(importErrorSchema),
  /** Pkt 58: wiersze pominięte, ale nieblokujące importu (powtórzony, błędny lub podejrzany e‑mail, konto o innym nazwisku). */
  warnings: z.array(importErrorSchema).default([]),
  /** Pkt 58: osoby bez e‑maila – pominięte („Zosia Iwanek (wiersz 4)”). */
  withoutEmail: z.array(z.string()).default([]),
  /** Pkt 58: edycje dnia, w których szukano symboli („Koderek · Warszawa”). */
  editions: z.array(z.string()).default([]),
});
export type CoSpeakersReport = z.infer<typeof coSpeakersReportSchema>;

export const importRunSchema = z.object({
  id: z.string().uuid(),
  at: z.string(),
  source: z.enum(importSources),
  dryRun: z.boolean(),
  success: z.boolean(),
  created: z.number().int(),
  updated: z.number().int(),
  errors: z.number().int(),
  actor: z.string().nullable(),
});
export type ImportRun = z.infer<typeof importRunSchema>;
export const importRunListSchema = z.object({ imports: z.array(importRunSchema) });
export const importRunDetailSchema = importRunSchema.extend({ report: z.union([coSpeakersReportSchema, importReportSchema]) });

// --- Agenda (US-301, US-302) ---

export const agendaActivitySchema = z.object({
  id: z.string().uuid(),
  symbol: z.string(),
  title: z.string(),
  typeId: z.string().uuid(),
  roomId: z.string().uuid(),
  startsAt: z.string(),
  endsAt: z.string(),
  audience: audienceSchema,
  ageMin: z.number().int().nullable(),
  ageMax: z.number().int().nullable(),
  /** BR-22: zapisy tylko dla typu z zapisami i limitu > 0; inaczej „Wstęp wolny”. */
  enrollment: z.boolean(),
  capacity: z.number().int(),
  reserve: z.number().int(),
  enrolled: z.number().int(),
  status: z.enum(activityStatuses),
  cancelReason: z.string().nullable(),
  description: z.string().nullable(),
  // Bez `requirements` – wymagania prowadzącego są dla organizatorów (panel admina), nie dla uczestników (decyzja 2026‑09‑28).
  /** US-307 AC-1: tekst do wyświetlenia albo imiona i nazwiska prowadzących. */
  speakers: z.string().nullable(),
  speakerBios: z.array(z.object({ name: z.string(), bio: z.string() })),
});
export type AgendaActivity = z.infer<typeof agendaActivitySchema>;

export const agendaSchema = z.object({
  edition: editionSummarySchema,
  rooms: z.array(z.object({ id: z.string().uuid(), name: z.string(), hidden: z.boolean() })),
  types: z.array(
    z.object({ id: z.string().uuid(), name: z.string(), color: z.string(), hasEnrollment: z.boolean(), blocksTime: z.boolean() }),
  ),
  activities: z.array(agendaActivitySchema),
});
export type Agenda = z.infer<typeof agendaSchema>;

/**
 * BR-32: czy uczestnik mieści się w grupie zajęć. Dziecko – wiek w roku edycji w przedziale (brzegi opcjonalne),
 * dorosły – tylko na zajęcia dla dorosłych.
 */
export function fitsAudience(
  activity: { audience: Audience; ageMin: number | null; ageMax: number | null },
  participant: { kind: 'child' | 'adult'; birthYear: number | null },
  editionYear: number,
): boolean {
  if (participant.kind === 'adult') return activity.audience !== 'children';
  if (activity.audience === 'adults' || participant.birthYear === null) return false;
  const age = ageInYear(participant.birthYear, editionYear);
  return (activity.ageMin === null || age >= activity.ageMin) && (activity.ageMax === null || age <= activity.ageMax);
}

/** US-301 AC-2: opis wieku na kafelku. */
export function audienceLabel(activity: { audience: Audience; ageMin: number | null; ageMax: number | null }): string {
  if (activity.audience === 'adults') return 'dla dorosłych';
  const suffix = activity.audience === 'children_adults' ? ' i dorośli' : '';
  if (activity.ageMin !== null && activity.ageMax !== null) return `${activity.ageMin}–${activity.ageMax} lat${suffix}`;
  if (activity.ageMin !== null) return `od ${activity.ageMin} lat${suffix}`;
  if (activity.ageMax !== null) return `do ${activity.ageMax} lat${suffix}`;
  return activity.audience === 'children_adults' ? 'bez ograniczeń' : 'dla dzieci';
}
